import { Popover as PopoverAnt } from 'antd';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React, { createContext, useImperativeHandle, useState } from 'react';
import { usePortalZIndex } from '../../hooks/usePortalZIndex';
import './popover.component.scss';

export const PopoverContext = createContext<{
  onClose: () => void;
}>({ onClose: () => {} });

export declare namespace PopoverType {
  type Props = {
    className?: string | null;
    children: React.ReactNode;
    triggerElement: React.ReactNode | string;
    triggerEvent?: 'click' | 'hover';
    autoClose?: boolean | null;
    placement?:
      | 'topLeft'
      | 'top'
      | 'topRight'
      | 'leftTop'
      | 'left'
      | 'leftBottom'
      | 'rightTop'
      | 'right'
      | 'rightBottom'
      | 'bottomLeft'
      | 'bottom'
      | 'bottomRight';
    minWidth?: number | null;
    maxWidth?: number | null;
    withBorder?: boolean | null;
    withPadding?: boolean | null;
    gap?: number | null;
  };
}

export const Popover = observer(
  React.forwardRef<{ setOpen: (value: boolean) => void }, PopoverType.Props>(
    (
      {
        className,
        children,
        triggerElement,
        placement = 'bottomLeft',
        triggerEvent = 'click',
        minWidth,
        maxWidth,
        withBorder = false,
        withPadding = true,
        autoClose = true,
        gap,
      }: PopoverType.Props,
      ref,
    ) => {
      const [open, setOpen] = useState<boolean>(false);
      const zIndex = usePortalZIndex(open);

      useImperativeHandle(ref, () => ({
        setOpen: (value: boolean) => setOpen(value),
      }));

      const gapValue = gap ?? 8;

      const getOffset = (): [number, number] => {
        if (placement.startsWith('bottom')) {
          return [0, gapValue];
        } else if (placement.startsWith('top')) {
          return [0, -gapValue];
        } else if (placement.startsWith('right')) {
          return [gapValue, 0];
        } else if (placement.startsWith('left')) {
          return [-gapValue, 0];
        }
        return [0, 0];
      };

      return (
        <PopoverContext.Provider
          value={{
            onClose: () => {
              if (autoClose) {
                setOpen(() => false);
              }
            },
          }}
        >
          <PopoverAnt
            onOpenChange={(value) => {
              if (autoClose) {
                setOpen(value);
              }
            }}
            open={open}
            rootClassName={classNames(`popover`, {
              'popover--padding': withPadding,
              'popover--border': withBorder,
            })}
            style={{
              minWidth: minWidth ? `${minWidth}px` : 'initial',
              maxWidth: maxWidth ? `${maxWidth}px` : 'initial',
            }}
            arrow={false}
            className={classNames(className)}
            content={children}
            trigger={triggerEvent}
            placement={placement}
            destroyOnHidden={true}
            popupVisible={true}
            getPopupContainer={() => document.body}
            zIndex={zIndex ?? undefined}
            align={{
              offset: getOffset(),
            }}
          >
            <div
              onClick={(e) => {
                e.stopPropagation();
                setOpen(true);
              }}
              className="popover__button"
            >
              {triggerElement}
            </div>
          </PopoverAnt>
        </PopoverContext.Provider>
      );
    },
  ),
);
